import {
  RouterProvider,
  createBrowserRouter,
  RouteObject,
} from "react-router-dom"; // 1. Import RouteObject
import ProtectedRoute from "@/features/auth/routes/ProtectedRoutes";
import AppLayout from "@/layout/AppLayout";
import FullScreenLoader from "@/components/feedback/full-screen-loader/FullScreenLoader";
import RouteErrorFallback from "@/components/feedback/route-error/RouteErrorFallback";
import { buildDynamicRoutes } from "@/routes/buildDynamicRoutes";
import { AppNotFound, RootCatchAll } from "@/routes/RouteFallbacks";
import useRouterData from "@/hooks/api/useRouterData";
import { AUTH_REDIRECT, LIVE_BOARD, LOGIN, NOT_FOUND_PATH, PROFILE, STUDENT_PORTAL_PUBLIC } from "@/config/path";
import AuthRedirect from "@/hooks/api/AuthRedirection";
import { useMemo, Suspense } from "react";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import Login from "@/components/layout/login/Login";
import UserProfilePage from "@/components/layout/profile/UserProfile";
import { useAppSelector } from "@/stores/hook";
import { hrRoutes } from "@/features/hr/routes/hrRoutes";
import { isPhoneShell } from "@/mobile/shell";
import MobileLayout from "@/mobile/MobileLayout";
import { buildMobileRoutes } from "@/mobile/routes/buildMobileRoutes";

const StudentPortalPublicPage = lazyWithRetry(
  () => import("@/page/studentPortal/StudentPortalPublicPage"),
);

const LiveBoardPage = lazyWithRetry(
  () => import("@/features/Dashboard/v2/liveBoard/LiveBoardPage"),
);

const SharedViewPage = lazyWithRetry(
  () => import("@/page/core/sharedViews/SharedViewPage"),
);
const CourseFinderPage = lazyWithRetry(
  () => import("@/page/niche/education/courseFinder/CourseFinderPage"),
);
const CourseDetailPage = lazyWithRetry(
  () => import("@/page/niche/education/courseFinder/CourseDetailPage"),
);

export default function AppRouterProvider() {
  // Subscribe to the auth token for the RE-RENDER only — it is deliberately
  // NOT a dep of the router memo below. Login/logout tear down the tabs query
  // (`resetRoutesForNewSession` / `resetQueryClientForLogout`) while this
  // component's observer is still mounted, and React Query does NOT notify an
  // active observer when its query is removed: the observer keeps serving its
  // last result until the component renders again and re-attaches to the
  // cache. Nothing else above the router subscribes to Redux, so without this
  // line a sign-in could leave `tabs` frozen (undefined or the outgoing
  // tenant's), the router without a "/" route — and the user on a permanent
  // 404 right after "Welcome back".
  useAppSelector((state) => state.auth.token);
  const { tabs } = useRouterData();

  // Deps are `[tabs]` ONLY. Auth/loading state used to sit here too, which
  // rebuilt the whole router (and remounted the app) on every token or
  // fetch-state flip. RootCatchAll reads that state itself instead.
  const router = useMemo(() => {
    // 2. Explicitly type this array as RouteObject[]
    const routes: RouteObject[] = [
      {
        path: AUTH_REDIRECT,
        element: <AuthRedirect />,
      },
      {
        path: LOGIN,
        element: <Login />,
      },
      {
        path: PROFILE,
        element: <UserProfilePage />,
      },
      {
        path: STUDENT_PORTAL_PUBLIC,
        element: (
          <Suspense fallback={<FullScreenLoader />}>
            <StudentPortalPublicPage />
          </Suspense>
        ),
      },
      {
        // The live board: one widget on its own page, outside the CRM layout,
        // with its own sign-in popup (features/Dashboard/v2/liveBoard).
        path: LIVE_BOARD,
        element: (
          <Suspense fallback={<FullScreenLoader />}>
            <LiveBoardPage />
          </Suspense>
        ),
      },
    ];

    if (tabs) {
      // The phone shell (src/mobile) renders the mobile app's UI around the
      // same tabs; decided once per page load from the screen, never resized.
      const phone = isPhoneShell();
      const dynamicRoutes = phone ? buildMobileRoutes(tabs) : buildDynamicRoutes(tabs);

      routes.push({
        path: "/",
        element: <ProtectedRoute />,
        children: [
          {
            path: "/",
            element: phone ? <MobileLayout /> : <AppLayout />,
            errorElement: <RouteErrorFallback />,
            children: [
              // We need to cast dynamicRoutes because spread sometimes confuses TS too
              ...(dynamicRoutes as RouteObject[]),
              // HR Suite — hardcoded client-side, NOT server-driven from /platform/tabs.
              // Each route is RBAC-gated on its own route key; see features/hr/config/hr.nav.ts.
              ...hrRoutes,
              {
                // Share links (features/sharedViews) — outside the tab config.
                path: "shared/:token",
                element: (
                  <Suspense fallback={<FullScreenLoader />}>
                    <SharedViewPage />
                  </Suspense>
                ),
              },
              {
                path: "leads/:leadId/course-finder",
                element: (
                  <Suspense fallback={<FullScreenLoader />}>
                    <CourseFinderPage />
                  </Suspense>
                ),
              },
              {
                path: "leads/:leadId/course-finder/:courseId",
                element: (
                  <Suspense fallback={<FullScreenLoader />}>
                    <CourseDetailPage />
                  </Suspense>
                ),
              },
              {
                // Not a bare 404: a stale link from a previous tenant/user must
                // redirect home, and a route table mid-rebuild must show the
                // loader. See RouteFallbacks.AppNotFound.
                path: "*",
                element: <AppNotFound />,
              },
            ],
          },
        ],
      });
    }

    routes.push({
      path: NOT_FOUND_PATH,
      element: <RootCatchAll />,
    });

    return createBrowserRouter(routes);
  }, [tabs]);

  if (!router) return <FullScreenLoader />;

  return <RouterProvider router={router} />;
}
